import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

export default function ProjectFilesManager() {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('all')

  useEffect(() => {
    loadFiles()
  }, [])

  async function loadFiles() {
    try {
      setLoading(true)
      setError('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_project_files_admin_list', {
        p_project_id: null,
        p_client_id: null
      })

      if (rpcError) throw rpcError

      setFiles(data || [])
    } catch (err) {
      console.error(err)
      setError('Σφάλμα κατά τη φόρτωση των αρχείων project.')
    } finally {
      setLoading(false)
    }
  }

  const filteredFiles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return files.filter((file) => {
      const matchesSearch =
        !term ||
        file.file_name?.toLowerCase().includes(term) ||
        file.project_title?.toLowerCase().includes(term) ||
        file.client_name?.toLowerCase().includes(term) ||
        file.file_type?.toLowerCase().includes(term)

      const matchesVisibility =
        visibilityFilter === 'all' ||
        (visibilityFilter === 'visible' && file.is_client_visible) ||
        (visibilityFilter === 'hidden' && !file.is_client_visible)

      return matchesSearch && matchesVisibility
    })
  }, [files, searchTerm, visibilityFilter])

  async function toggleVisibility(file) {
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_project_file_set_client_visibility', {
        p_file_id: file.file_id,
        p_is_visible: !file.is_client_visible
      })

      if (rpcError) throw rpcError

      await loadFiles()
      setSuccess('Η ορατότητα του αρχείου ενημερώθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενημέρωση ορατότητας αρχείου: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  function visibilityBadgeClasses(isVisible) {
    return isVisible
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  function fileTypeLabel(fileType) {
    if (!fileType) return '—'
    return fileType
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Project Files</h1>
        <p className="mt-1 text-sm text-gray-600">
          Διαχείριση αρχείων έργων και ορατότητας στο client portal.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
          {success}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiSearch}
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Όνομα αρχείου, project, πελάτης, τύπος..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Portal Visibility</label>
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">Όλα</option>
            <option value="visible">Ορατά</option>
            <option value="hidden">Κρυφά</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <SafeIcon icon={FiIcons.FiFileText} className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p>Δεν βρέθηκαν αρχεία.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Αρχείο
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Πελάτης
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Τύπος
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Portal
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Ενέργειες
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredFiles.map((file) => (
                  <tr key={file.file_id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{file.file_name}</div>
                      <div className="text-xs text-gray-500">
                        {file.created_at ? new Date(file.created_at).toLocaleString('el-GR') : '—'}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {file.project_title || '—'}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {file.client_name || '—'}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {fileTypeLabel(file.file_type)}
                    </td>

                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${visibilityBadgeClasses(file.is_client_visible)}`}>
                        {file.is_client_visible ? 'Ορατό' : 'Κρυφό'}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                        >
                          Άνοιγμα
                        </a>

                        <button
                          onClick={() => toggleVisibility(file)}
                          disabled={saving}
                          className={`rounded-lg px-3 py-2 text-sm disabled:opacity-50 ${
                            file.is_client_visible
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {file.is_client_visible ? 'Απόκρυψη' : 'Εμφάνιση'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}